import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Chip,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import PrintIcon from "@mui/icons-material/Print";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import TagIcon from "@mui/icons-material/Tag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import MuiCard from "../../components/mui/MuiCard";
import { getInvoices } from "../../helpers/api_helper";
import moment from "moment";

/**
 * MuiInvoiceDetail - Invoice detail page with MUI components
 *
 * UX Laws Applied:
 * - Law of Proximity: Related information grouped in sections
 * - Visual Hierarchy: Important totals highlighted with larger text
 * - Fitts's Law: Action buttons clearly visible and easily clickable
 * - Aesthetic-Usability Effect: Clean, modern design improves usability perception
 */

// Status configuration
const statusConfig = {
  paid: { bg: "#34c38f", icon: CheckCircleIcon, label: "Paid" },
  pending: { bg: "#f1b44c", icon: AccessTimeIcon, label: "Pending" },
  overdue: { bg: "#f46a6a", icon: AccessTimeIcon, label: "Overdue" },
  cancelled: { bg: "#f46a6a", icon: AccessTimeIcon, label: "Cancelled" },
  draft: { bg: "#74788d", icon: AccessTimeIcon, label: "Draft" },
  default: { bg: "#556ee6", icon: AccessTimeIcon, label: "Unknown" },
};

// Styled components
const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const TotalRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isGrandTotal",
})(({ theme, isGrandTotal }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1.5, 0),
  borderTop: isGrandTotal ? `2px solid ${theme.palette.divider}` : "none",
  marginTop: isGrandTotal ? theme.spacing(1) : 0,
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => {
  const config = statusConfig[statuscolor] || statusConfig.default;
  return {
    backgroundColor: alpha(config.bg, 0.15),
    color: config.bg,
    fontWeight: 600,
    fontSize: "0.875rem",
    height: 32,
    borderRadius: 8,
    "& .MuiChip-icon": {
      color: config.bg,
    },
  };
});

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

const MuiInvoiceDetail = () => {
  const params = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [totals, setTotals] = useState({});
  const [products, setProducts] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState({});

  document.title = `Invoice ${invoiceDetails.number || ""} | FlexFlow ERP`;

  // Fetch invoice details on component mount
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await getInvoices(params.id);
        if (res && res[0]) {
          const invoice = res[0];

          // Parse content and totals fields
          const parsedContent = JSON.parse(invoice.content || "[]");
          const parsedTotals = JSON.parse(invoice.totals || "{}");

          setProducts(parsedContent);
          setTotals(parsedTotals);
          setProvider(invoice.providername || "Unknown Provider");
          setInvoiceDetails(invoice);
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [params.id]);

  // Breadcrumb configuration
  const breadcrumbs = [
    { label: "Invoices", path: "/invoices" },
    { label: `Invoice #${invoiceDetails.number || "..."}` },
  ];

  // Get status configuration
  const status = invoiceDetails.status?.toLowerCase() || "pending";
  const statusInfo = statusConfig[status] || statusConfig.default;
  const StatusIcon = statusInfo.icon;

  // Action buttons
  const headerActions = (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Print Invoice">
        <IconButton
          onClick={() => window.print()}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <PrintIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Send Email">
        <IconButton
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <EmailIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download PDF">
        <IconButton
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Tooltip>
      <Button
        variant="contained"
        color="primary"
        startIcon={<EditIcon />}
        sx={{ borderRadius: 2 }}
      >
        Edit
      </Button>
    </Stack>
  );

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ py: 3 }}>
        <Container maxWidth={false}>
          <MuiPageHeader
            title="Invoice Details"
            breadcrumbs={breadcrumbs}
            loading={true}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false}>
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/invoices")}
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>

        {/* Page Header */}
        <MuiPageHeader
          title={`Invoice #${invoiceDetails.number || "-"}`}
          subtitle={`Created on ${moment(invoiceDetails.created).format("DD MMMM YYYY")}`}
          breadcrumbs={breadcrumbs}
          actions={headerActions}
        />

        <Grid container spacing={3}>
          {/* Invoice Header Card */}
          <Grid item xs={12} md={8}>
            <MuiCard
              title="Invoice Information"
              subtitle="Basic invoice details"
              showDivider
            >
              <Grid container spacing={3}>
                {/* Invoice Details */}
                <Grid item xs={12} sm={6}>
                  <InfoItem>
                    <InfoIcon>
                      <ReceiptIcon fontSize="small" />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {invoiceDetails.number || "-"}
                      </Typography>
                    </Box>
                  </InfoItem>

                  <InfoItem>
                    <InfoIcon>
                      <TagIcon fontSize="small" />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body1">
                        {invoiceDetails.type || "-"}
                      </Typography>
                    </Box>
                  </InfoItem>

                  <InfoItem>
                    <InfoIcon>
                      <CalendarTodayIcon fontSize="small" />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Import Date
                      </Typography>
                      <Typography variant="body1">
                        {invoiceDetails.importDate
                          ? moment(invoiceDetails.importDate).format("DD/MM/YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                  </InfoItem>
                </Grid>

                {/* Provider & Status */}
                <Grid item xs={12} sm={6}>
                  <InfoItem>
                    <InfoIcon>
                      <BusinessIcon fontSize="small" />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Provider
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {provider}
                      </Typography>
                      {invoiceDetails.providervat && (
                        <Typography variant="caption" color="text.secondary">
                          AFM: {invoiceDetails.providervat}
                        </Typography>
                      )}
                    </Box>
                  </InfoItem>

                  <InfoItem>
                    <InfoIcon>
                      <PersonIcon fontSize="small" />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Entered By
                      </Typography>
                      <Typography variant="body1">
                        {invoiceDetails.enteredby || "-"}
                      </Typography>
                    </Box>
                  </InfoItem>

                  {invoiceDetails.refcode && (
                    <InfoItem>
                      <InfoIcon>
                        <TagIcon fontSize="small" />
                      </InfoIcon>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Reference Code
                        </Typography>
                        <Chip
                          label={invoiceDetails.refcode}
                          size="small"
                          color="success"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </InfoItem>
                  )}
                </Grid>
              </Grid>
            </MuiCard>
          </Grid>

          {/* Status & Totals Card */}
          <Grid item xs={12} md={4}>
            <MuiCard title="Summary" showDivider>
              {/* Status Badge */}
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <StatusChip
                  label={statusInfo.label}
                  statuscolor={status}
                  icon={<StatusIcon />}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Totals */}
              <Box>
                <TotalRow>
                  <Typography variant="body2" color="text.secondary">
                    Items Count
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {totals.totalQuantity || 0}
                  </Typography>
                </TotalRow>

                <TotalRow>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(totals.totalPrdAmmount)}
                  </Typography>
                </TotalRow>

                <TotalRow>
                  <Typography variant="body2" color="text.secondary">
                    VAT
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(totals.totalVat)}
                  </Typography>
                </TotalRow>

                <TotalRow isGrandTotal>
                  <Typography variant="h6" fontWeight={600}>
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    {formatCurrency(totals.totalOrder)}
                  </Typography>
                </TotalRow>
              </Box>
            </MuiCard>
          </Grid>

          {/* Products Table */}
          <Grid item xs={12}>
            <MuiCard
              title="Line Items"
              subtitle={`${products.length} product${products.length !== 1 ? "s" : ""}`}
              showDivider
              noPadding
            >
              <TableContainer component={Paper} elevation={0}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "grey.50",
                        "& th": {
                          fontWeight: 600,
                          color: "text.secondary",
                          fontSize: "0.8125rem",
                        },
                      }}
                    >
                      <TableCell>#</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">VAT</TableCell>
                      <TableCell align="center">Stock</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length > 0 ? (
                      products.map((product, index) => (
                        <TableRow
                          key={product.id || index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {product.name || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {product.description || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={product.sku || "-"}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.category || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={product.quantity || 0}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.unit || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={500}>
                              {formatCurrency(product.totalPrdAmmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(product.vatProducts)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={product.stock || 0}
                              size="small"
                              color={
                                product.stock > 10
                                  ? "success"
                                  : product.stock > 0
                                  ? "warning"
                                  : "error"
                              }
                              variant="filled"
                              sx={{ fontSize: "0.75rem", minWidth: 40 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                          <ReceiptIcon
                            sx={{ fontSize: 48, color: "grey.300", mb: 1 }}
                          />
                          <Typography color="text.secondary">
                            No products found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MuiCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

MuiInvoiceDetail.propTypes = {};

export default MuiInvoiceDetail;
